import { styled } from "goober";
import { useEffect } from "react";
import {
  isArchiveMediaType,
  isCsvMediaType,
  isSqliteMediaType,
} from "../../../shared/attachmentPolicy";
import { formatBytes } from "../../lib/formatBytes";
import { officePreviewFormat } from "../../lib/officePreview";
import { CsvPreview } from "../CsvPreview";
import { Text } from "../Text";
import { Archive } from "./Archive";
import { Audio } from "./Audio";
import { Binary } from "./Binary";
import { Email } from "./Email";
import { Html } from "./Html";
import { Http } from "./Http";
import { Image } from "./Image";
import { Json } from "./Json";
import { Markdown } from "./Markdown";
import { Office } from "./Office";
import { Pdf } from "./Pdf";
import { Plain } from "./Plain";
import type {
  FilePreviewTarget,
  FileRenderContent,
  FileRenderControl,
  FileSelection,
} from "./types";
import { useUrlContent, PREVIEW_BYTES } from "./useUrlContent";

export type {
  FilePreviewTarget,
  FileRenderContent,
  FileRenderControl,
  FileSelection,
} from "./types";
import { SqlitePreview } from "../SqlitePreview";
import { Video } from "./Video";
import type { InspectOptions } from "../LiveBrowser/useInspectMode";

const TruncatedBanner = styled("div")`
  padding: 8px 12px;
  border-top: 1px solid var(--alpha8);
  background: var(--alpha4);
`;

const Empty = styled("div")`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
`;

export type FileRenderProps = {
  url: string;
  fileName?: string;
  sizeBytes?: number;
  // The container's padding; full-bleed renderers (html, archive, http, json
  // table) cancel it to reach the container's edges.
  bleed?: number;
  baseRoute?: string;
  // Render HTML as an interactive, scrollable viewport instead of the static,
  // scaled-down card. Opt-in (not the default) so the interactive-untrusted-
  // HTML surface stays confined to callers that ask for it. Only Html honors it.
  interactive?: boolean;
  htmlInspection?: Omit<InspectOptions, "enabled">;
  selectedRanges?: readonly { from: number; to: number; color?: string }[];
  emptyState?: React.ReactNode;
  onSelect?: (selections: FileSelection[]) => void;
  onControls?: (controls: FileRenderControl[]) => void;
  onContent?: (content: FileRenderContent) => void;
  onOpenFile?: (file: FilePreviewTarget) => void;
};

export function FileRender({
  url,
  fileName,
  sizeBytes,
  bleed,
  baseRoute,
  interactive,
  htmlInspection,
  selectedRanges,
  emptyState,
  onSelect,
  onControls,
  onContent,
  onOpenFile,
}: FileRenderProps) {
  const { loading, failed, contentType, content, size, truncated } =
    useUrlContent(url);
  const totalSize = size ?? sizeBytes;

  const downloadContentType =
    contentType != null &&
    (content != null ||
      contentType === "message/http" ||
      contentType === "message/rfc822" ||
      isCsvMediaType(contentType) ||
      isSqliteMediaType(contentType))
      ? contentType
      : null;
  useEffect(() => {
    onContent?.({ text: content, truncated, downloadContentType });
  }, [onContent, content, truncated, downloadContentType]);

  if (failed) return <Empty>Couldn’t load this file</Empty>;
  if (loading || !contentType) return null;

  const officeFormat = officePreviewFormat(contentType, fileName);
  if (officeFormat) {
    return (
      <Office
        key={`${url}:${officeFormat}`}
        url={url}
        fileName={fileName}
        format={officeFormat}
      />
    );
  }

  if (contentType.startsWith("image/")) {
    return (
      <Image
        url={url}
        contentType={contentType}
        fileName={fileName}
        onControls={onControls}
      />
    );
  }
  if (contentType.startsWith("audio/")) {
    return (
      <Audio
        url={url}
        contentType={contentType}
        fileName={fileName}
        onControls={onControls}
      />
    );
  }
  if (contentType.startsWith("video/")) {
    return (
      <Video
        url={url}
        contentType={contentType}
        fileName={fileName}
        onControls={onControls}
      />
    );
  }
  if (contentType === "application/pdf") {
    return <Pdf url={url} fileName={fileName} onControls={onControls} />;
  }
  if (contentType === "message/http") {
    return (
      <Http
        url={url}
        bleed={bleed}
        onControls={onControls}
        onOpenFile={onOpenFile}
        onSelect={onSelect}
        selectedRanges={selectedRanges}
      />
    );
  }
  if (contentType === "message/rfc822") {
    return <Email url={url} bleed={bleed} onControls={onControls} />;
  }
  if (isCsvMediaType(contentType)) {
    return (
      <CsvPreview
        enabled
        sizeBytes={totalSize ?? 0}
        url={url}
        fileName={fileName}
      />
    );
  }
  if (isSqliteMediaType(contentType)) {
    return <SqlitePreview enabled url={url} />;
  }
  if (isArchiveMediaType(contentType)) {
    return (
      <Archive
        url={url}
        contentType={contentType}
        fileName={fileName}
        bleed={bleed}
        onControls={onControls}
        onOpenFile={onOpenFile}
      />
    );
  }

  if (content == null) {
    return (
      <Binary
        url={url}
        contentType={contentType}
        fileName={fileName}
        onControls={onControls}
      />
    );
  }

  if (content === "") return emptyState ?? <Empty>Empty file</Empty>;

  const body =
    contentType === "application/json" ||
    contentType === "application/jsonl" ? (
      <Json
        output={content}
        bleed={bleed}
        selectedRanges={selectedRanges}
        onSelect={onSelect}
        onControls={onControls}
      />
    ) : contentType === "text/markdown" ? (
      <Markdown
        output={content}
        selectedRanges={selectedRanges}
        onSelect={onSelect}
        onControls={onControls}
      />
    ) : contentType === "text/html" ? (
      <Html
        key={url}
        output={content}
        fileName={fileName}
        bleed={bleed}
        baseRoute={baseRoute}
        interactive={interactive}
        inspection={htmlInspection}
      />
    ) : (
      <Plain
        output={content}
        fileName={fileName}
        selectedRanges={selectedRanges}
        onSelect={onSelect}
      />
    );

  return (
    <>
      {body}
      {truncated && totalSize != null && (
        <TruncatedBanner>
          <Text variant="text-small" color="var(--text-secondary)">
            Content truncated – showing first {formatBytes(PREVIEW_BYTES)} of{" "}
            {formatBytes(totalSize)}
          </Text>
        </TruncatedBanner>
      )}
    </>
  );
}
